import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useAIAssistant } from "../context/AIAssistantContext";

const navItems = [
  { id: "home", key: "home", type: "section" },
  { id: "services", key: "services", type: "section" },
  { id: "products", key: "products", type: "route", to: "/productos" },
  { id: "workflow", key: "workflows", type: "section" },
  { id: "why", key: "solutions", type: "section" },
  { id: "contact", key: "contact", type: "section" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const { openAssistant } = useAIAssistant();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNavigation = (item) => {
    setOpen(false);
    if (item.type === "route") {
      navigate(item.to);
      return;
    }
    scrollTo(item.id);
  };

  const navLabel = (item) =>
    item.key === "products" ? (lang === "es" ? "Productos" : "Products") : t.nav[item.key];

  const handleAssistant = () => {
    setOpen(false);
    openAssistant();
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#09090b]/80 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 sm:h-24 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("home")}
          className="flex items-center gap-3 group"
          aria-label="FIKAMI — Home"
        >
          <img
            src="/fikami-logo.svg"
            alt="FIKAMI"
            className="h-10 sm:h-12 w-auto object-contain select-none"
            style={{ width: "auto" }}
            draggable={false}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-0.5">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => handleNavigation(item)}
              className={`px-3 py-2 text-sm transition-colors ${
                item.type === "route" && location.pathname.startsWith("/productos")
                  ? "text-[#FF7A2B]"
                  : "text-zinc-300 hover:text-white"
              }`}
            >
              {navLabel(item)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div
            className="hidden sm:flex items-center rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden"
            data-testid="lang-switcher"
          >
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-1.5 text-xs font-semibold tracking-widest transition-all ${
                lang === "en"
                  ? "bg-[#FF5A00] text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
            <span className="w-px h-4 bg-white/10" />
            <button
              onClick={() => setLang("es")}
              data-testid="lang-es"
              className={`px-3 py-1.5 text-xs font-semibold tracking-widest transition-all ${
                lang === "es"
                  ? "bg-[#FF5A00] text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              ES
            </button>
          </div>

          <button
            onClick={handleAssistant}
            data-testid="nav-cta"
            className="hidden sm:inline-flex fk-btn fk-btn-primary !py-2 !px-4 !text-xs"
          >
            <i className="fa-solid fa-wand-magic-sparkles" />
            {t.nav.aiCta}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((value) => !value)}
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white"
            aria-label="Menu"
          >
            <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"} text-sm`} />
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/5 bg-[#09090b]/95 backdrop-blur-xl"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => handleNavigation(item)}
                className="text-left px-3 py-3 rounded-lg text-zinc-200 hover:bg-white/5 hover:text-white"
              >
                {navLabel(item)}
              </button>
            ))}

            <button
              onClick={handleAssistant}
              data-testid="mobile-nav-cta"
              className="mt-2 fk-btn fk-btn-primary justify-center"
            >
              <i className="fa-solid fa-wand-magic-sparkles" />
              {t.nav.aiCta}
            </button>

            <div className="flex items-center gap-2 pt-3 mt-2 border-t border-white/5">
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`flex-1 py-2 rounded-full text-xs font-semibold tracking-widest ${
                  lang === "en"
                    ? "bg-[#FF5A00] text-white"
                    : "border border-white/10 text-zinc-300"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("es")}
                data-testid="mobile-lang-es"
                className={`flex-1 py-2 rounded-full text-xs font-semibold tracking-widest ${
                  lang === "es"
                    ? "bg-[#FF5A00] text-white"
                    : "border border-white/10 text-zinc-300"
                }`}
              >
                ES
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
